<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ColorInput from 'dashboard/components-next/colorpicker/ColorInput.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import ImageInput from 'dashboard/components-next/image-input/ImageInput.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { ATTRIBUTE_CATEGORIES } from 'dashboard/routes/dashboard/settings/attributes/constants';

const props = defineProps({
  selectedNode: { type: Object, default: null },
  selectedChromeId: { type: String, default: null },
  components: { type: Array, default: () => [] },
  theme: { type: Object, default: () => ({}) },
  portalEditableAttributeKeys: { type: Array, default: () => [] },
  eventAttributeOptions: { type: Array, default: () => [] },
  embedded: { type: Boolean, default: false },
});
const emit = defineEmits([
  'close',
  'update-node',
  'update-layout-settings',
  'update:portalEditableAttributeKeys',
  'open-settings',
]);

const { t } = useI18n();

const COLUMN_COUNT_OPTIONS = [2, 3, 4, 5, 6].map(n => ({
  value: n,
  label: String(n),
}));

const GAP_OPTIONS = [
  { value: 0, label: 'None' },
  { value: 2, label: 'XS' },
  { value: 4, label: 'S' },
  { value: 6, label: 'M' },
  { value: 8, label: 'L' },
  { value: 10, label: 'XL' },
  { value: 12, label: '2XL' },
];

const PADDING_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'sm', label: 'S' },
  { value: 'md', label: 'M' },
  { value: 'lg', label: 'L' },
  { value: 'xl', label: 'XL' },
];

const FONT_SIZE_OPTIONS = [
  { value: '', label: 'Auto' },
  { value: 'sm', label: 'S' },
  { value: 'base', label: 'M' },
  { value: 'lg', label: 'L' },
  { value: 'xl', label: 'XL' },
];

const SELECT_OPTION_LABELS = {
  sm: 'S',
  md: 'M',
  lg: 'L',
  breakdown: 'Breakdown',
  total: 'Total',
  button: 'Button',
  scribble: 'Scribble',
  type_name: 'Type name',
  email_verified: 'Email verified',
  1: 'H1',
  2: 'H2',
  3: 'H3',
  primary: 'Primary',
  secondary: 'Secondary',
  outline: 'Outline',
  line: 'Line',
  space: 'Space',
  dots: 'Dots',
  overlay: 'Overlay',
  card: 'Card',
};

const isLayout = computed(
  () =>
    props.selectedNode &&
    (props.selectedNode.kind === 'columns' ||
      props.selectedNode.kind === 'section')
);

const isBlock = computed(
  () =>
    props.selectedNode &&
    (props.selectedNode.kind === 'block' || props.selectedNode.kind === 'html')
);

const isTab = computed(
  () => props.selectedNode && props.selectedNode.kind === 'tab'
);

const isAttributesForm = computed(
  () =>
    props.selectedNode &&
    props.selectedNode.kind === 'block' &&
    props.selectedNode.tagName === 'attributes-form'
);

const componentDefs = computed(() => {
  const map = {};
  props.components.forEach(c => {
    map[c.tag_name] = c;
  });
  return map;
});

const currentDef = computed(() => {
  if (!props.selectedNode || props.selectedNode.kind !== 'block') return null;
  return componentDefs.value[props.selectedNode.tagName] || null;
});

const propDefinitions = computed(() => currentDef.value?.props || {});

const columnCount = computed(() => props.selectedNode?.children?.length || 2);

const categoryOptions = computed(() =>
  ATTRIBUTE_CATEGORIES.map(item => ({
    value: item.key.toLowerCase(),
    label: t(`ATTRIBUTES_MGMT.ATTRIBUTE_CATEGORIES.${item.key}`),
  }))
);

const textAlignOptions = computed(() => [
  {
    value: '',
    label: t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.ALIGN_NONE'),
  },
  {
    value: 'left',
    label: t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.ALIGN_LEFT'),
  },
  {
    value: 'center',
    label: t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.ALIGN_CENTER'),
  },
  {
    value: 'right',
    label: t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.ALIGN_RIGHT'),
  },
]);

const cleanDefault = value => {
  if (value == null) return '';
  const str = String(value);
  return str.replace(/^["']|["']$/g, '');
};

const propValue = (propKey, propDef) => {
  const current = props.selectedNode?.props?.[propKey];
  if (current != null && current !== '') return current;
  return cleanDefault(propDef?.default);
};

const isImageProp = (propKey, propDef) =>
  propDef?.type === 'image' || propKey === 'src';

const selectOptionLabel = opt => SELECT_OPTION_LABELS[opt] || opt;

const selectOptionsForProp = propDef =>
  (propDef.options || []).map(opt => ({
    value: opt,
    label: selectOptionLabel(opt),
  }));

const parseCategories = value => {
  if (!value || value === '""') return [];
  return String(value)
    .split(',')
    .map(v => v.trim())
    .filter(Boolean);
};

const categoriesModel = propKey =>
  parseCategories(props.selectedNode?.props?.[propKey]);

const updateProp = (key, value) => {
  if (!props.selectedNode) return;
  emit('update-node', {
    id: props.selectedNode.id,
    props: { [key]: value },
  });
};

const updateCategories = (propKey, values) => {
  updateProp(propKey, (values || []).join(','));
};

const normalizeHex = value => {
  if (!value || value === 'transparent') return '';
  const raw = String(value).trim().toLowerCase();
  if (!raw) return '';
  return raw.startsWith('#') ? raw : `#${raw}`;
};

const themeBgColor = computed(
  () => normalizeHex(props.theme?.bg_color) || '#ffffff'
);
const themeTextColor = computed(
  () => normalizeHex(props.theme?.text_color) || '#1e293b'
);

const hasColorOverride = key => {
  const value = props.selectedNode?.props?.[key];
  return Boolean(value && value !== 'transparent');
};

const hasBgOverride = computed(() => hasColorOverride('_bg'));
const hasTextOverride = computed(() => hasColorOverride('_text-color'));

const hasAnyColorOverride = computed(
  () => hasBgOverride.value || hasTextOverride.value
);

const effectiveBgColor = computed(() => {
  if (hasBgOverride.value) return props.selectedNode.props._bg;
  return themeBgColor.value;
});

const effectiveTextColor = computed(() => {
  if (hasTextOverride.value) return props.selectedNode.props['_text-color'];
  return themeTextColor.value;
});

const updateThemeLinkedColor = (propKey, themeHex, value) => {
  if (!value || value === 'transparent') {
    updateProp(propKey, '');
    return;
  }
  if (normalizeHex(value) === normalizeHex(themeHex)) {
    updateProp(propKey, '');
    return;
  }
  updateProp(propKey, value);
};

const updateBgColor = value => {
  updateThemeLinkedColor('_bg', themeBgColor.value, value);
};

const updateTextColor = value => {
  updateThemeLinkedColor('_text-color', themeTextColor.value, value);
};

const resetColorToTheme = propKey => {
  updateProp(propKey, '');
};

const updateContent = value => {
  if (!props.selectedNode) return;
  emit('update-node', {
    id: props.selectedNode.id,
    content: value,
  });
};

const updateLayoutColumnCount = value => {
  if (!props.selectedNode || props.selectedNode.kind !== 'columns') return;
  emit('update-layout-settings', {
    columnCount: Number(value),
    gap: props.selectedNode.gap ?? 6,
  });
};

const updateLayoutGap = value => {
  if (!props.selectedNode || props.selectedNode.kind !== 'columns') return;
  emit('update-layout-settings', {
    columnCount: columnCount.value,
    gap: Number(value),
  });
};

const layoutTagHint = computed(() =>
  props.selectedNode?.kind === 'section' ? '<portal-section>' : '<row>'
);

const tabTagHint = '<portal-tab>';
</script>

<template>
  <component
    :is="embedded ? 'div' : 'aside'"
    :class="
      embedded
        ? 'flex flex-col min-h-0'
        : 'w-[300px] shrink-0 border-l border-n-slate-5 bg-white flex flex-col overflow-hidden'
    "
  >
    <div
      v-if="!embedded"
      class="flex items-center justify-between h-12 px-4 border-b border-n-slate-5 shrink-0"
    >
      <p class="text-xs font-semibold text-n-slate-12">
        {{ $t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.TITLE') }}
      </p>
      <button
        v-if="selectedNode || selectedChromeId"
        class="flex items-center justify-center size-7 rounded-md text-n-slate-9 hover:bg-n-slate-3 hover:text-n-slate-12 transition-colors"
        :title="$t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.DESELECT')"
        @click="emit('close')"
      >
        <span class="i-lucide-x size-3.5" />
      </button>
    </div>

    <div
      v-else-if="selectedNode || selectedChromeId"
      class="flex items-center justify-end px-3 py-1.5 border-b border-n-slate-5 shrink-0"
    >
      <button
        class="flex items-center justify-center size-7 rounded-md text-n-slate-9 hover:bg-n-slate-3 hover:text-n-slate-12 transition-colors"
        :title="$t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.DESELECT')"
        @click="emit('close')"
      >
        <span class="i-lucide-x size-3.5" />
      </button>
    </div>

    <div class="flex-1 overflow-y-auto">
      <div
        v-if="selectedChromeId === 'portal-feedback'"
        class="flex flex-col px-4 py-6 gap-3"
      >
        <p class="text-sm font-semibold text-n-slate-12">
          {{ $t('PLANNING.EVENT_PORTALS.EDITOR.SETTINGS.FEEDBACK_TITLE') }}
        </p>
        <p class="text-xs text-n-slate-9 leading-relaxed">
          {{ $t('PLANNING.EVENT_PORTALS.EDITOR.SETTINGS.FEEDBACK_HINT') }}
        </p>
        <button
          type="button"
          class="text-xs font-medium text-n-brand-11 hover:underline text-left"
          @click="emit('open-settings')"
        >
          {{ $t('PLANNING.EVENT_PORTALS.EDITOR.SETTINGS.OPEN') }}
        </button>
      </div>
      <div
        v-else-if="selectedChromeId === 'portal-assistant'"
        class="flex flex-col px-4 py-6 gap-3"
      >
        <p class="text-sm font-semibold text-n-slate-12">
          {{ $t('PLANNING.EVENT_PORTALS.EDITOR.SETTINGS.WIDGET_TITLE') }}
        </p>
        <p class="text-xs text-n-slate-9 leading-relaxed">
          {{ $t('PLANNING.EVENT_PORTALS.EDITOR.SETTINGS.WIDGET_HINT') }}
        </p>
        <button
          type="button"
          class="text-xs font-medium text-n-brand-11 hover:underline text-left"
          @click="emit('open-settings')"
        >
          {{ $t('PLANNING.EVENT_PORTALS.EDITOR.SETTINGS.OPEN') }}
        </button>
      </div>
      <div
        v-else-if="!selectedNode"
        class="flex flex-col items-center justify-center px-6 py-16 text-center"
      >
        <span
          class="flex items-center justify-center size-12 rounded-2xl bg-n-slate-2 text-n-slate-8 mb-3"
        >
          <span class="i-lucide-mouse-pointer-click size-5" />
        </span>
        <p class="text-sm font-medium text-n-slate-12 mb-1">
          {{ $t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.NO_SELECTION') }}
        </p>
        <p class="text-xs text-n-slate-9 leading-relaxed">
          {{ $t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.NO_SELECTION_HINT') }}
        </p>
      </div>

      <div v-else-if="isLayout" class="p-4 space-y-4">
        <div class="pb-3 border-b border-n-slate-5">
          <p class="text-sm font-semibold text-n-slate-12">
            {{
              selectedNode.kind === 'section'
                ? $t('PLANNING.EVENT_PORTALS.EDITOR.SECTION')
                : $t('PLANNING.EVENT_PORTALS.EDITOR.COLUMNS')
            }}
          </p>
          <p class="text-[11px] text-n-slate-9 mt-0.5 font-mono">
            {{ layoutTagHint }}
          </p>
        </div>

        <template v-if="selectedNode.kind === 'columns'">
          <div class="grid grid-cols-[88px_1fr] items-center gap-x-2 gap-y-2">
            <label class="text-xs font-medium text-n-slate-11 truncate">
              {{ $t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.COLUMNS') }}
            </label>
            <div class="min-w-0">
              <Input
                type="select"
                size="xs"
                :model-value="columnCount"
                :options="COLUMN_COUNT_OPTIONS"
                @update:model-value="updateLayoutColumnCount"
              />
            </div>

            <label class="text-xs font-medium text-n-slate-11 truncate">
              {{ $t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.GAP') }}
            </label>
            <div class="min-w-0">
              <Input
                type="select"
                size="xs"
                :model-value="selectedNode.gap ?? 6"
                :options="GAP_OPTIONS"
                @update:model-value="updateLayoutGap"
              />
            </div>
          </div>
        </template>

        <p v-else class="text-xs text-n-slate-9">
          {{ $t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.SECTION_HINT') }}
        </p>
      </div>

      <div v-else-if="isTab" class="p-4 space-y-4">
        <div class="pb-3 border-b border-n-slate-5">
          <p class="text-sm font-semibold text-n-slate-12">
            {{ $t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.TAB') }}
          </p>
          <p class="text-[11px] text-n-slate-9 mt-0.5 font-mono">
            {{ tabTagHint }}
          </p>
        </div>
        <div class="grid grid-cols-[88px_1fr] items-center gap-x-2">
          <label class="text-xs font-medium text-n-slate-11 truncate">
            {{ $t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.TAB_NAME') }}
          </label>
          <input
            type="text"
            :value="selectedNode.props?.name || ''"
            class="reset-base min-w-0 w-full px-2.5 py-1.5 text-sm border border-n-slate-5 rounded-lg bg-n-slate-1 text-n-slate-12 outline-none focus:border-n-brand-7"
            @input="updateProp('name', $event.target.value)"
          />
        </div>
      </div>

      <div v-else-if="isBlock" class="p-4 space-y-4">
        <template v-if="selectedNode.kind === 'block'">
          <div class="pb-3 border-b border-n-slate-5">
            <p class="text-sm font-semibold text-n-slate-12">
              {{ currentDef?.label || selectedNode.tagName }}
            </p>
            <p class="text-[11px] text-n-slate-9 mt-0.5 font-mono">
              &lt;{{ selectedNode.tagName }}&gt;
            </p>
          </div>

          <div
            v-if="
              selectedNode.tagName &&
              selectedNode.tagName.startsWith('content-') &&
              selectedNode.tagName !== 'content-image'
            "
            class="space-y-1.5"
          >
            <label class="block text-xs font-medium text-n-slate-11">
              {{ $t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.CONTENT') }}
            </label>
            <textarea
              :value="selectedNode.content || ''"
              rows="3"
              class="reset-base w-full px-2.5 py-2 text-sm border border-n-slate-5 rounded-lg bg-n-slate-1 text-n-slate-12 resize-y outline-none focus:border-n-brand-7"
              :placeholder="
                $t(
                  'PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.CONTENT_PLACEHOLDER'
                )
              "
              @input="updateContent($event.target.value)"
            />
          </div>

          <div v-if="isAttributesForm" class="space-y-2">
            <label class="block text-xs font-medium text-n-slate-11">
              {{ $t('PLANNING.EVENT_PORTALS.FORM.EDITABLE_ATTRIBUTES') }}
            </label>
            <p class="text-[11px] text-n-slate-9">
              {{ $t('PLANNING.EVENT_PORTALS.FORM.EDITABLE_ATTRIBUTES_HINT') }}
            </p>
            <TagMultiSelectComboBox
              :model-value="portalEditableAttributeKeys"
              :options="eventAttributeOptions"
              :placeholder="
                $t(
                  'PLANNING.EVENT_PORTALS.FORM.EDITABLE_ATTRIBUTES_PLACEHOLDER'
                )
              "
              @update:model-value="
                emit('update:portalEditableAttributeKeys', $event || [])
              "
            />
          </div>

          <div
            v-if="Object.keys(propDefinitions).length"
            class="grid grid-cols-[88px_1fr] items-center gap-x-2 gap-y-2"
          >
            <template
              v-for="(propDef, propKey) in propDefinitions"
              :key="propKey"
            >
              <template v-if="isImageProp(propKey, propDef)">
                <label class="text-xs font-medium text-n-slate-11 truncate">
                  {{ propDef.label || propKey }}
                </label>
                <div class="min-w-0">
                  <ImageInput
                    :model-value="propValue(propKey, propDef)"
                    @update:model-value="updateProp(propKey, $event)"
                  />
                </div>
              </template>

              <template v-else>
                <label class="text-xs font-medium text-n-slate-11 truncate">
                  {{ propDef.label || propKey }}
                </label>

                <div class="min-w-0">
                  <TagMultiSelectComboBox
                    v-if="propDef.type === 'multiselect'"
                    :model-value="categoriesModel(propKey)"
                    :options="categoryOptions"
                    :placeholder="
                      $t(
                        'PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.CATEGORIES_ALL'
                      )
                    "
                    @update:model-value="updateCategories(propKey, $event)"
                  />

                  <Input
                    v-else-if="propDef.type === 'select'"
                    type="select"
                    size="xs"
                    :model-value="propValue(propKey, propDef)"
                    :options="selectOptionsForProp(propDef)"
                    @update:model-value="updateProp(propKey, $event)"
                  />

                  <div
                    v-else-if="propDef.type === 'boolean'"
                    class="flex items-center h-8"
                  >
                    <input
                      type="checkbox"
                      :checked="propValue(propKey, propDef) === 'true'"
                      class="reset-base rounded border-n-slate-5"
                      @change="
                        updateProp(
                          propKey,
                          $event.target.checked ? 'true' : 'false'
                        )
                      "
                    />
                  </div>

                  <ColorInput
                    v-else-if="propDef.type === 'color'"
                    :model-value="
                      colorModel(propKey) || propDef.default || '#000000'
                    "
                    @update:model-value="updateColorProp(propKey, $event)"
                  />

                  <input
                    v-else
                    type="text"
                    :value="propValue(propKey, propDef)"
                    class="reset-base w-full px-2.5 py-1.5 text-sm border border-n-slate-5 rounded-lg bg-n-slate-1 text-n-slate-12 outline-none focus:border-n-brand-7"
                    @input="updateProp(propKey, $event.target.value)"
                  />
                </div>
              </template>
            </template>
          </div>

          <p
            v-else-if="
              !isAttributesForm &&
              !(
                selectedNode.tagName &&
                selectedNode.tagName.startsWith('content-') &&
                selectedNode.tagName !== 'content-image'
              )
            "
            class="text-xs text-n-slate-9"
          >
            {{ $t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.NO_OPTIONS') }}
          </p>

          <div
            class="pt-3 border-t border-n-slate-5 grid grid-cols-[88px_1fr] items-center gap-x-2 gap-y-2"
          >
            <div class="col-span-2 flex items-center justify-between gap-2">
              <p
                class="text-[10px] font-semibold uppercase tracking-wider text-n-slate-9"
              >
                {{ $t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.STYLE') }}
              </p>
              <p class="text-[10px] text-n-slate-9">
                {{
                  hasAnyColorOverride
                    ? $t(
                        'PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.STYLE_CUSTOM'
                      )
                    : $t(
                        'PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.STYLE_USING_THEME'
                      )
                }}
              </p>
            </div>

            <label class="text-xs font-medium text-n-slate-11 truncate">
              {{ $t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.BACKGROUND') }}
            </label>
            <div class="min-w-0 flex items-center gap-1">
              <div class="min-w-0 flex-1">
                <ColorInput
                  :model-value="effectiveBgColor"
                  @update:model-value="updateBgColor"
                />
              </div>
              <button
                v-if="hasBgOverride"
                type="button"
                class="shrink-0 px-1.5 py-1 text-[10px] font-medium text-n-slate-10 hover:text-n-slate-12 hover:bg-n-slate-3 rounded-md transition-colors"
                :title="
                  $t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.USE_THEME')
                "
                @click="resetColorToTheme('_bg')"
              >
                {{ $t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.USE_THEME') }}
              </button>
            </div>

            <label class="text-xs font-medium text-n-slate-11 truncate">
              {{ $t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.PADDING') }}
            </label>
            <div class="min-w-0">
              <Input
                type="select"
                size="xs"
                :model-value="selectedNode.props?.['_padding'] || ''"
                :options="PADDING_OPTIONS"
                @update:model-value="updateProp('_padding', $event)"
              />
            </div>

            <label class="text-xs font-medium text-n-slate-11 truncate">
              {{ $t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.TEXT_COLOR') }}
            </label>
            <div class="min-w-0 flex items-center gap-1">
              <div class="min-w-0 flex-1">
                <ColorInput
                  :model-value="effectiveTextColor"
                  @update:model-value="updateTextColor"
                />
              </div>
              <button
                v-if="hasTextOverride"
                type="button"
                class="shrink-0 px-1.5 py-1 text-[10px] font-medium text-n-slate-10 hover:text-n-slate-12 hover:bg-n-slate-3 rounded-md transition-colors"
                :title="
                  $t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.USE_THEME')
                "
                @click="resetColorToTheme('_text-color')"
              >
                {{ $t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.USE_THEME') }}
              </button>
            </div>

            <label class="text-xs font-medium text-n-slate-11 truncate">
              {{ $t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.FONT_SIZE') }}
            </label>
            <div class="min-w-0">
              <Input
                type="select"
                size="xs"
                :model-value="selectedNode.props?.['_font-size'] || ''"
                :options="FONT_SIZE_OPTIONS"
                @update:model-value="updateProp('_font-size', $event)"
              />
            </div>

            <label class="text-xs font-medium text-n-slate-11 truncate">
              {{ $t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.ALIGNMENT') }}
            </label>
            <div class="min-w-0">
              <Input
                type="select"
                size="xs"
                :model-value="selectedNode.props?.['_text-align'] || ''"
                :options="textAlignOptions"
                @update:model-value="updateProp('_text-align', $event)"
              />
            </div>
          </div>
        </template>

        <template v-else>
          <div class="pb-3 border-b border-n-slate-5">
            <p class="text-sm font-semibold text-n-slate-12">
              {{ $t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.HTML_BLOCK') }}
            </p>
          </div>
          <textarea
            :value="selectedNode.content"
            class="reset-base w-full h-48 px-3 py-2 text-sm font-mono border border-n-slate-5 rounded-lg bg-n-slate-1 text-n-slate-12 resize-y outline-none focus:border-n-brand-7"
            @input="updateContent($event.target.value)"
          />
        </template>
      </div>

      <div v-else class="p-4 space-y-3">
        <div class="pb-3 border-b border-n-slate-5">
          <p class="text-sm font-semibold text-n-slate-12 capitalize">
            {{ selectedNode.kind }}
          </p>
          <p
            v-if="selectedNode.tagName"
            class="text-[11px] text-n-slate-9 mt-0.5 font-mono"
          >
            &lt;{{ selectedNode.tagName }}&gt;
          </p>
        </div>
        <p class="text-xs text-n-slate-9">
          {{ $t('PLANNING.EVENT_PORTALS.EDITOR.PROPERTIES.REARRANGE_HINT') }}
        </p>
      </div>
    </div>
  </component>
</template>
